/**
 * The harbour: geography, obstacles, and named berths.
 *
 * One bay, sheltered by two breakwaters, with three working areas — the fuel
 * jetty to the west, the container terminal to the north, and an anchorage out
 * beyond the heads where barges arrive. The Blender harbour follows the same
 * rotated box footprints as collision geometry. Procedural docks remain as
 * the loading/error fallback; buoys continue to follow the simulated water.
 */

import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  SphereGeometry,
  Vector3,
} from 'three/webgpu'
import type { Obstacle } from './Obstacle.ts'
import { sampleWaterHeight } from './waves.ts'
import { disposeHarbourModel, loadHarbourModel } from './harbourModel.ts'

export interface Berth {
  id: string
  label: string
  position: Vector3
  /** Heading the barge should end up on, radians. */
  heading: number
  /** Acceptance radius in metres. */
  radius: number
}

interface QuaySpec extends Obstacle {
  height: number
  color: string
}

/** Quays, breakwaters and pier heads. Also the collision world, verbatim. */
const QUAYS: QuaySpec[] = [
  // North container terminal: one long quay face.
  { x: 0, z: 340, halfWidth: 260, halfLength: 40, rotation: 0, height: 4, color: '#6b6f75' },
  // West fuel jetty: a finger pier poking south-east.
  { x: -300, z: 60, halfWidth: 18, halfLength: 90, rotation: 0.5, height: 3.4, color: '#77695a' },
  // Breakwaters guarding the bay, with a gap between the heads.
  { x: -180, z: -270, halfWidth: 210, halfLength: 16, rotation: -0.28, height: 2.8, color: '#565d63' },
  { x: 265, z: -220, halfWidth: 160, halfLength: 16, rotation: 0.42, height: 2.8, color: '#565d63' },
  // East mole closing the bay off from the open sea.
  { x: 385, z: 60, halfWidth: 16, halfLength: 230, rotation: -0.1, height: 3, color: '#565d63' },
]

/** Fairway buoys: green to starboard entering, red to port (IALA A). */
const BUOYS: { x: number; z: number; color: string }[] = [
  { x: 40, z: -260, color: '#1f9d44' },
  { x: -60, z: -255, color: '#c8262b' },
  { x: 55, z: -120, color: '#1f9d44' },
  { x: -70, z: -110, color: '#c8262b' },
  { x: 60, z: 40, color: '#1f9d44' },
  { x: -75, z: 55, color: '#c8262b' },
]

export const BERTHS: Berth[] = [
  {
    id: 'terminal',
    label: 'Container Terminal, Berth 2',
    position: new Vector3(60, 0, 268),
    heading: Math.PI / 2,
    radius: 110,
  },
  {
    id: 'fuel',
    label: 'Fuel Jetty',
    position: new Vector3(-238, 0, 128),
    heading: 0.5 + Math.PI / 2,
    radius: 95,
  },
  {
    id: 'anchorage',
    label: 'Outer Anchorage',
    position: new Vector3(-40, 0, -520),
    heading: 0,
    radius: 170,
  },
]

/** Offshore pickup area, with room to turn the full 220 m hull clear of the heads. */
export const BARGE_SPAWNS: { position: Vector3; heading: number }[] = [
  { position: new Vector3(120, 0, -760), heading: 0.3 },
  { position: new Vector3(-220, 0, -800), heading: -0.2 },
  { position: new Vector3(-40, 0, -720), heading: 0 },
]

export const TUG_SPAWN = { position: new Vector3(-60, 0, 180), heading: Math.PI }

export class Harbour {
  readonly group = new Group()
  readonly obstacles: Obstacle[]
  private readonly staticGroup = new Group()
  private modelLoad: Promise<void> | null = null
  private disposed = false
  private readonly buoyMeshes: { mesh: Mesh; x: number; z: number; phase: number }[] = []

  constructor() {
    this.group.name = 'harbour'
    this.staticGroup.name = 'harbour-static'
    this.group.add(this.staticGroup)
    this.obstacles = QUAYS

    for (const quay of QUAYS) {
      const material = new MeshStandardNodeMaterial({ color: new Color(quay.color) })
      material.roughness = 0.95
      const mesh = new Mesh(new BoxGeometry(quay.halfWidth * 2, quay.height + 6, quay.halfLength * 2), material)
      // Boxes extend 6 m below the waterline so waves never peek underneath.
      mesh.position.set(quay.x, (quay.height - 6) / 2 + 1, quay.z)
      mesh.rotation.y = -quay.rotation
      mesh.castShadow = true
      mesh.receiveShadow = true
      this.staticGroup.add(mesh)

      // A rubbing strake along the top edge, so quay faces read at a distance.
      const capMaterial = new MeshStandardNodeMaterial({ color: new Color('#2c3136') })
      capMaterial.roughness = 0.8
      const cap = new Mesh(new BoxGeometry(quay.halfWidth * 2 + 0.6, 0.5, quay.halfLength * 2 + 0.6), capMaterial)
      cap.position.set(quay.x, quay.height + 1.05, quay.z)
      cap.rotation.y = -quay.rotation
      this.staticGroup.add(cap)
    }

    // Container blocks on the terminal, for skyline.
    const boxColors = ['#8a4a2f', '#33648e', '#476f45', '#96772c']
    for (let i = 0; i < 12; i++) {
      const material = new MeshStandardNodeMaterial({ color: new Color(boxColors[i % boxColors.length]) })
      material.roughness = 0.85
      const stack = new Mesh(new BoxGeometry(24, 5 + (i % 3) * 2.6, 14), material)
      stack.position.set(-220 + i * 40, 5 + 2 + (i % 3) * 1.3, 355 + (i % 2) * 22)
      stack.castShadow = true
      this.staticGroup.add(stack)
    }

    // Cranes: two gantry silhouettes over the terminal quay.
    const craneMaterial = new MeshStandardNodeMaterial({ color: new Color('#b6412e') })
    craneMaterial.roughness = 0.6
    for (const cx of [-80, 120]) {
      const crane = new Group()
      for (const lx of [-14, 14]) {
        const leg = new Mesh(new BoxGeometry(2.4, 34, 2.4), craneMaterial)
        leg.position.set(lx, 17, 0)
        crane.add(leg)
      }
      const beam = new Mesh(new BoxGeometry(34, 2.8, 3), craneMaterial)
      beam.position.set(0, 34, 0)
      crane.add(beam)
      const jib = new Mesh(new BoxGeometry(3, 2.4, 46), craneMaterial)
      jib.position.set(0, 32, -20)
      crane.add(jib)
      crane.position.set(cx, 4, 305)
      this.staticGroup.add(crane)
    }

    for (const [index, buoy] of BUOYS.entries()) {
      const material = new MeshStandardNodeMaterial({ color: new Color(buoy.color) })
      material.roughness = 0.5
      const mesh = new Mesh(new CylinderGeometry(1.1, 1.5, 2.6, 10), material)
      mesh.position.set(buoy.x, 0.6, buoy.z)
      this.group.add(mesh)

      const topMaterial = new MeshStandardNodeMaterial({ color: new Color('#e8e5da') })
      topMaterial.emissive = new Color(buoy.color)
      topMaterial.emissiveIntensity = 0.8
      const top = new Mesh(new SphereGeometry(0.45, 8, 8), topMaterial)
      top.position.y = 1.7
      mesh.add(top)

      this.buoyMeshes.push({ mesh, x: buoy.x, z: buoy.z, phase: index * 1.7 })
    }
  }

  /** Start only for an active harbour, keeping physics-only construction cheap. */
  loadModel(): Promise<void> {
    if (this.disposed) return Promise.resolve()
    return this.modelLoad ??= loadHarbourModel().then(model => {
      if (this.disposed) {
        disposeHarbourModel(model)
        return
      }
      disposeHarbourModel(this.staticGroup)
      this.staticGroup.add(model)
    }).catch(error => {
      if (!this.disposed) console.warn('Harbour GLB failed to load; keeping procedural docks:', error)
    })
  }

  dispose(): void {
    this.disposed = true
    disposeHarbourModel(this.group)
    this.buoyMeshes.length = 0
  }

  /** Bob the buoys on the same water everything else floats on. */
  update(elapsed: number): void {
    for (const buoy of this.buoyMeshes) {
      const height = sampleWaterHeight(buoy.x, buoy.z, elapsed)
      buoy.mesh.position.y = height + 0.6
      buoy.mesh.rotation.x = Math.sin(elapsed * 0.9 + buoy.phase) * 0.07
      buoy.mesh.rotation.z = Math.cos(elapsed * 0.7 + buoy.phase) * 0.07
    }
  }
}
